import { TablePaginationConfig } from "antd";
import { useState, useMemo, useEffect, useCallback } from "react";
import { RouteComponentProps } from "react-router-dom";
import { arraysAreEqual } from "services/utils/formatterUtil";

interface UsePaginationTableProps {
  initialPage?: number;
  initialPageSize?: number;
  initialSort?: string[];
  initialFilter?: string;
  persistInUrl?: boolean;
  debounceDelay?: number;
}

export interface UsePaginationTableReturn<T> {
  // Estados
  currentPage: number;
  pageSize: number;
  sort: string[];
  filterText: string;
  debouncedFilterText: string;
  totalItems: number;
  items: T[];

  // Setters
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSort: (sort: string[]) => void;
  setFilterText: (filter: string) => void;
  setTotalItems: (total: number) => void;
  setItems: (items: T[]) => void;

  // Funciones de utilidad
  onPageChange: (page: number, pageSize?: number) => void;
  onPageSizeChange: (current: number, size: number) => void;
  onSortChange: (sort: string[]) => void;
  resetPagination: () => void;
  globalReset: () => void;

  paginationConfig: {
    current: number;
    pageSize: number;
    total: number;
    showSizeChanger: boolean;
    showTotal: (total: number, range: [number, number]) => string;
    pageSizeOptions: string[];
  };
}

// Función helper para debounce
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Funcion helper para paginar tablas automaticamente sin volver a pedir al backend la siguiente pagina
export const useSelfPagination = (data: unknown[], pageSize = 10) => {
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  const paginationConfig: TablePaginationConfig = useMemo(
    () => ({
      current: currentPage,
      pageSize,
      showSizeChanger: false,
      showTotal: (total) => `Total ${total} registros`,
      onChange: (page: number) => setCurrentPage(page),
    }),
    [currentPage, pageSize]
  );

  return paginationConfig;
};

// Función para parsear query params
const parseQueryParams = (
  search: string
): {
  page?: number;
  pageSize?: number;
  filter?: string;
  sort?: string[];
} => {
  const params = new URLSearchParams(search);

  const page = params.get("page");
  const pageSize = params.get("pageSize");
  const filter = params.get("filter");
  const sort = params.get("sort");

  return {
    ...(page && { page: parseInt(page, 10) }),
    ...(pageSize && { pageSize: parseInt(pageSize, 10) }),
    ...(filter && { filter }),
    ...(sort && { sort: sort.split(",") }),
  };
};

export const usePaginationTable = <T>(
  routerProps: RouteComponentProps,
  {
    initialPage = 0,
    initialPageSize = 10,
    initialSort = ["id,DESC"],
    initialFilter = "",
    persistInUrl = true,
    debounceDelay = 500, // Delay por defecto de 500ms
  }: UsePaginationTableProps = {}
): UsePaginationTableReturn<T> => {
  const { location, history } = routerProps;

  // Obtener valores iniciales de query params si existen
  const queryParams = parseQueryParams(location.search);

  // Estados principales
  const [currentPage, setCurrentPageState] = useState<number>(
    queryParams.page !== undefined ? queryParams.page : initialPage
  );
  const [pageSize, setPageSizeState] = useState<number>(
    queryParams.pageSize !== undefined ? queryParams.pageSize : initialPageSize
  );
  const [sort, setSortState] = useState<string[]>(
    queryParams.sort !== undefined ? queryParams.sort : initialSort
  );
  const [filterText, setFilterTextState] = useState<string>(
    queryParams.filter !== undefined ? queryParams.filter : initialFilter
  );
  const [totalItems, setTotalItems] = useState<number>(0);
  const [items, setItems] = useState<T[]>([]);

  // Debounce para el filterText
  const debouncedFilterText = useDebounce(filterText.trim(), debounceDelay);

  // Función para actualizar URL
  const updateUrl = useCallback(
    (updates: {
      page?: number;
      pageSize?: number;
      filter?: string;
      sort?: string[];
    }) => {
      if (!persistInUrl) return;

      const currentParams = new URLSearchParams(location.search);
      const newParams = new URLSearchParams();

      // Copiar parámetros existentes
      for (const [key, value] of currentParams.entries()) {
        newParams.set(key, value);
      }

      // Aplicar actualizaciones
      if (updates.page !== undefined) {
        if (updates.page === initialPage) {
          newParams.delete("page");
        } else {
          newParams.set("page", updates.page.toString());
        }
      }

      if (updates.pageSize !== undefined) {
        if (updates.pageSize === initialPageSize) {
          newParams.delete("pageSize");
        } else {
          newParams.set("pageSize", updates.pageSize.toString());
        }
      }

      if (updates.filter !== undefined) {
        if (updates.filter === initialFilter) {
          newParams.delete("filter");
        } else {
          newParams.set("filter", updates.filter);
        }
      }

      if (updates.sort !== undefined) {
        if (JSON.stringify(updates.sort) === JSON.stringify(initialSort)) {
          newParams.delete("sort");
        } else {
          newParams.set("sort", updates.sort.join(","));
        }
      }

      const newSearch = newParams.toString();
      const currentSearch = location.search.replace("?", "");

      // Solo actualizar si hay cambios
      if (newSearch !== currentSearch) {
        history.replace({
          ...location,
          search: newSearch ? `?${newSearch}` : "",
        });
      }
    },
    [
      persistInUrl,
      location,
      history,
      initialPage,
      initialPageSize,
      initialFilter,
      initialSort,
    ]
  );

  // Setters que actualizan tanto el estado como la URL
  const setCurrentPage = useCallback(
    (page: number) => {
      if (currentPage === page) {
        return;
      }
      setCurrentPageState(page);
      if (persistInUrl) {
        updateUrl({ page });
      }
    },
    [persistInUrl, updateUrl]
  );

  const setPageSize = useCallback(
    (size: number) => {
      if (pageSize === size) {
        return;
      }
      setPageSizeState(size);
      if (persistInUrl) {
        updateUrl({ pageSize: size });
      }
    },
    [persistInUrl, updateUrl]
  );

  const setSort = useCallback(
    (newSort: string[]) => {
      if (arraysAreEqual(sort, newSort)) {
        return;
      }
      setSortState(newSort);
      if (persistInUrl) {
        updateUrl({ sort: newSort });
      }
    },
    [persistInUrl, updateUrl]
  );

  const setFilterText = useCallback(
    (filter: string) => {
      setFilterTextState(filter);
      if (persistInUrl) {
        updateUrl({ filter });
      }
    },
    [persistInUrl, updateUrl]
  );

  // Manejo de cambio de página (Antd usa base 1)
  const onPageChange = useCallback(
    (page: number, newPageSize?: number) => {
      setCurrentPage(page - 1);
      if (newPageSize && newPageSize !== pageSize) {
        setPageSize(newPageSize);
      }
    },
    [setCurrentPage, setPageSize, pageSize]
  );

  // Manejo de cambio de tamaño de página
  const onPageSizeChange = useCallback(
    (current: number, size: number) => {
      setPageSize(size);
      setCurrentPage(0);
    },
    [setPageSize, setCurrentPage]
  );

  // Manejo de ordenamiento
  const onSortChange = useCallback(
    (newSort: string[]) => {
      setSort(newSort);
      setCurrentPage(0);
    },
    [setSort, setCurrentPage]
  );

  // Reset de paginación
  const resetPagination = useCallback(() => {
    setCurrentPage(initialPage);
    setPageSize(initialPageSize);
    setSort(initialSort);
    setFilterText(initialFilter);
  }, [
    setCurrentPage,
    setPageSize,
    setSort,
    setFilterText,
    initialPage,
    initialPageSize,
    initialSort,
    initialFilter,
  ]);

  // Reset de paginación
  const globalReset = useCallback(() => {
    setCurrentPage(initialPage);
    setPageSize(initialPageSize);
    setSort(initialSort);
    setFilterText(initialFilter);
    setTotalItems(0);
    setItems([]);
  }, [
    setCurrentPage,
    setPageSize,
    setSort,
    setFilterText,
    setTotalItems,
    setItems,
    initialPage,
    initialPageSize,
    initialSort,
    initialFilter,
  ]);

  // Configuración de paginación para componentes Antd
  const paginationConfig = useMemo(
    () => ({
      current: currentPage + 1,
      pageSize,
      total: totalItems,
      showSizeChanger: true,
      showTotal: (total: number) => `Total ${total} elementos`,
      pageSizeOptions: ["10", "20", "50", "100"],
    }),
    [currentPage, pageSize, totalItems]
  );

  return {
    // Estados
    currentPage,
    pageSize,
    sort,
    filterText,
    debouncedFilterText,
    totalItems,
    items,

    // Setters
    setCurrentPage,
    setPageSize,
    setSort,
    setFilterText,
    setTotalItems,
    setItems,

    // Funciones
    onPageChange,
    onPageSizeChange,
    onSortChange,
    resetPagination,
    globalReset,

    // Configuración
    paginationConfig,
  };
};
